import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BookText, Users, Building2, LogOut, ArrowRight, LineChart, ShoppingCart, Tag, Store } from "lucide-react";

const Card = ({ icon: Icon, title, desc, onClick, testid, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left rounded-2xl border border-border bg-card p-8 shadow-sm hover:shadow-md hover:border-primary transition-all duration-200 active:scale-[0.99]"
  >
    <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon className="h-7 w-7 text-white" />
    </div>
    <h2 className="font-heading text-2xl font-semibold tracking-tight mt-6">{title}</h2>
    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
    <div className="flex items-center gap-1.5 text-sm font-medium text-primary mt-6 group-hover:gap-2.5 transition-all">
      Openen <ArrowRight className="h-4 w-4" />
    </div>
  </button>
);

export default function ModuleHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="module-hub">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm">Singh Express Shop BV</div>
              <div className="text-xs text-muted-foreground">BE 0788.952.072</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" data-testid="hub-logout" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Kies een module</h1>
        <p className="text-muted-foreground mt-2">Waarmee wil je aan de slag?</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          <Card
            testid="module-boekhouding"
            icon={BookText}
            accent="bg-primary"
            title="Boekhouding"
            desc="Digitaal dagboek van ontvangsten, Z-tickets, BTW-rapporten en het centrale kasboek."
            onClick={() => navigate("/boekhouding")}
          />
          <Card
            testid="module-hr"
            icon={Users}
            accent="bg-teal-600"
            title="HR Management"
            desc="Werknemersdocumenten in bulk uploaden en loonbrieven automatisch per werknemer sorteren via tekstextractie en OCR."
            onClick={() => navigate("/hr")}
          />
          <Card
            testid="module-kassa"
            icon={Store}
            accent="bg-blue-600"
            title="Kassa"
            desc="Webkassa voor Singh Express Shop Brustem: verkopen, kortingen, BTW, kassabon, onveranderbaar register en Z-afsluiting."
            onClick={() => navigate("/kassa")}
          />
          <Card
            testid="module-resultaten"
            icon={LineChart}
            accent="bg-emerald-600"
            title="Resultaten"
            desc="Winst- en verliesrekening per periode op basis van de bevestigde grootboekboekingen, met PDF- en Excel-export. Beveiligd met wachtwoord en e-mailcode."
            onClick={() => navigate("/resultaten")}
          />
          <Card
            testid="module-purchases"
            icon={ShoppingCart}
            accent="bg-amber-600"
            title="Purchases"
            desc="Rookwaren bestellen bij Colruyt Professionals per winkel: VTEC-verkoopanalyse, automatisch bestelvoorstel, bestelbon-PDF en reservatiemail."
            onClick={() => navigate("/purchases")}
          />
          <Card
            testid="module-price-tags"
            icon={Tag}
            accent="bg-rose-600"
            title="Price Tags"
            desc="Prijskaartjes maken: foto's los, in bulk of als PDF uploaden, productnaam automatisch via AI, prijs zelf instellen en één afdrukbare A4-PDF (3×4 cm) genereren."
            onClick={() => navigate("/price-tags")}
          />
        </div>
      </main>
    </div>
  );
}
